import { computed, effect, inject } from "@angular/core";

import { of, pipe, switchMap, tap } from "rxjs";
import { catchError } from "rxjs/operators";

import { AuthService } from "@core/services/auth.service";
import { LoggerService } from "@core/services/logger.service";
import { DocumentModel } from "@documentation/models/document.model";
import { DocumentEditorProxyService } from "@documentation/services/document-editor-proxy.service";
import { patchState, signalStore, withComputed, withMethods, withState } from "@ngrx/signals";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { ModalService } from "@shared/components/modal/modal.service";
import { ToastService } from "@shared/services/toast.service";

import { DocumentHttpService } from "../services/document-http.service";

interface DocumentsState {
  selectedDocumentDetail: DocumentModel | null;
  editedContent: string | null; // For tracking unsaved changes
  loading: boolean;
  error: string | null;
  isNavigatingSafely: boolean;
  updatedDocument: { id: string; timestamp: number } | null;
}

const initialState: DocumentsState = {
  selectedDocumentDetail: null,
  editedContent: null,
  loading: false,
  error: null,
  isNavigatingSafely: false,
  updatedDocument: null
};

export const DocumentsStore = signalStore(
  { providedIn: "root" },
  withState(initialState),
  withComputed(({ selectedDocumentDetail, editedContent }) => {
    const activeDocument = computed(() => {
      const doc = selectedDocumentDetail();
      if (doc) {
        return doc;
      }
      return null;
    });

    // Dirty when content differs from the loaded content (no context dependence, no heavy normalization)
    const isDirty = computed(() => {
      const doc = selectedDocumentDetail();
      const edited = editedContent();
      if (!doc) return false;
      if (edited === null) return false;
      return (doc.content || "") !== (edited || "");
    });

    return {
      activeDocument,
      isDirty
    };
  }),
  withMethods(
    (
      store,
      documentService = inject(DocumentHttpService),
      auth = inject(AuthService),
      modal = inject(ModalService),
      editorProxy = inject(DocumentEditorProxyService),
      toast = inject(ToastService),
      logger = inject(LoggerService)
    ) => {
      // Reset state when user changes (logout/login)
      let lastUserId: string | null = null;
      effect(() => {
        const user = auth.currentUser();
        const nextId = user?.id || null;
        if (nextId !== lastUserId) {
          patchState(store, initialState);
          lastUserId = nextId;
        }
      });
      const handleUpdateSuccess = (updatedDoc: DocumentModel): void => {
        patchState(store, {
          selectedDocumentDetail: updatedDoc,
          editedContent: null,
          loading: false
        });
      };

      const handleUpdateError = (errorMessage: string): void => {
        patchState(store, {
          error: errorMessage,
          loading: false
        });
      };

      // Read editor markdown and require a few consecutive identical reads to avoid transient states
      const waitForEditorStableMarkdown = async (
        timeoutMs: number = 1500,
        pollMs: number = 50,
        requiredStableReads: number = 3
      ): Promise<string | null> => {
        const start = Date.now();
        let last: string | null = null;
        let stableCount = 0;
        while (Date.now() - start < timeoutMs) {
          const curr = editorProxy.getCurrentContent();
          if (curr === null) {
            return null;
          }

          if (typeof curr === "string" && curr === last) {
            stableCount++;
          } else {
            stableCount = 1;
            last = typeof curr === "string" ? curr : null;
          }
          if (stableCount >= requiredStableReads && last != null) {
            logger.debug("[DocsStore] editor markdown is stable", {
              length: last.length,
              stableCount
            });
            return last;
          }
          await new Promise((r) => setTimeout(r, pollMs));
        }
        return last;
      };

      // Local helpers to avoid calling store-bound methods within other methods
      const ensureEditorDiffsResolvedLocal = async (): Promise<boolean> => {
        try {
          while (true) {
            if (!editorProxy.hasActiveDiffPreviews()) {
              return true;
            }
            const raw = await modal.confirmChoices(
              "Unresolved changes",
              "This document has unaccepted changes. Resolve all changes.",
              [
                { id: "accept", label: "Accept all", btnClass: "btn-outline-success" },
                { id: "reject", label: "Reject all", btnClass: "btn-outline-danger" },
                { id: "manual", label: "Resolve manually", btnClass: "btn-primary" }
              ]
            );
            const choice = raw === "accept" || raw === "reject" || raw === "manual" ? raw : null;
            if (!choice || choice === "manual") {
              return false;
            }
            await editorProxy.resolveDiffsAndWait?.(choice);
            if (choice === "accept") {
              toast.success("All changes accepted");
            } else if (choice === "reject") {
              toast.info("All changes rejected");
            }
          }
        } catch (e) {
          logger.warn("ensureEditorDiffsResolved failed", e);
          return false;
        }
      };

      const saveCurrentDocumentWithDiffResolutionLocal = async (): Promise<boolean> => {
        const diffsOk = await ensureEditorDiffsResolvedLocal();
        if (!diffsOk) return false;
        try {
          const doc = store.selectedDocumentDetail();
          if (!doc) {
            handleUpdateError("No document selected.");
            return false;
          }
          // Determine which images were removed by comparing old vs new content
          const extractImageObjectKeys = (content: string, documentId: string): Set<string> => {
            try {
              const keys = new Set<string>();
              // Look for proxied image URLs: /api/documents/:id/images/:objectKey?exp=...&sig=...
              const base = `/api/documents/${documentId}/images/`;
              const escapedBase = base.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
              // Capture until a terminator: whitespace, quote, ), ], }, or ? (query start)
              const regex = new RegExp(`${escapedBase}([^\\s"'\\)\\]\\}\\?]+)`, "g");
              let match: RegExpExecArray | null;
              while ((match = regex.exec(content)) !== null) {
                const encoded = match[1] || "";
                const objectKeyEncoded = encoded.split("?")[0];
                try {
                  const key = decodeURIComponent(objectKeyEncoded);
                  if (key) keys.add(key);
                } catch {}
              }
              return keys;
            } catch {
              return new Set<string>();
            }
          };

          // Pull latest markdown snapshot from editor after ensuring stability
          let contentToSave: string | null = null;
          try {
            const stableMd = await waitForEditorStableMarkdown(2000, 50, 3);
            if (typeof stableMd === "string") {
              contentToSave = stableMd;
            }
          } catch {}
          // Early-out if content is unchanged
          if ((doc.content || "") === (contentToSave || "")) {
            return true;
          }
          // Compute removed image keys prior to saving
          const beforeKeys = extractImageObjectKeys(doc.content || "", doc.id);
          const afterKeys = extractImageObjectKeys((contentToSave || "") as string, doc.id);
          const removedKeys: string[] = Array.from(beforeKeys).filter((k) => !afterKeys.has(k));
          patchState(store, { loading: true });
          const normalized = (contentToSave || "") as string;
          await new Promise<void>((resolve, reject) => {
            documentService.updateDocumentContent(doc.id, normalized).subscribe({
              next: (updatedDoc) => {
                logger.debug("[DocsStore] save completed", {
                  updatedLen: (updatedDoc.content || "").length
                });
                handleUpdateSuccess(updatedDoc);
                // Fire-and-forget deletion of any removed images
                try {
                  if (removedKeys.length > 0) {
                    removedKeys.forEach((key) => {
                      try {
                        documentService.deleteDocumentImage(doc.id, key).subscribe({ next: () => {}, error: () => {} });
                      } catch {}
                    });
                  }
                } catch {}
                toast.success("Document saved successfully");
                resolve();
              },
              error: () => {
                handleUpdateError("Failed to update content.");
                reject(new Error("Failed to update content."));
              }
            });
          });
          return true;
        } catch (e) {
          logger.warn("saveCurrentDocumentWithDiffResolution failed", e);
          return false;
        }
      };

      const handleUnsavedChangesBeforeNavigationLocal = async (): Promise<boolean> => {
        const hasUnsavedChanges = store.isDirty();
        if (!hasUnsavedChanges) return true;
        try {
          const choice = await modal.confirmChoices(
            "Unsaved Changes",
            "You have unsaved changes that will be lost. How would you like to proceed?",
            [
              { id: "cancel", label: "Cancel", btnClass: "btn-outline-secondary", buttonSize: "md" },
              { id: "ok", label: "OK", btnClass: "btn-outline-primary", buttonSize: "md" },
              { id: "save", label: "Save & Continue", btnClass: "btn-success", buttonSize: "md" }
            ]
          );
          if (choice === "cancel") return false;
          if (choice === "ok") {
            patchState(store, { isNavigatingSafely: true });
            return true;
          }
          if (choice === "save") {
            const saved = await saveCurrentDocumentWithDiffResolutionLocal();
            if (saved) {
              patchState(store, { isNavigatingSafely: true });
            }
            return saved;
          }
          return false;
        } catch (e) {
          logger.warn("handleUnsavedChangesBeforeNavigation failed", e);
          return false;
        }
      };

      return {
        ensureEditorDiffsResolved: ensureEditorDiffsResolvedLocal,
        saveCurrentDocumentWithDiffResolution: saveCurrentDocumentWithDiffResolutionLocal,
        handleUnsavedChangesBeforeNavigation: handleUnsavedChangesBeforeNavigationLocal,
        setEditedContent: (content: string): void => {
          patchState(store, { editedContent: content });
        },
        loadDocument: rxMethod<string>(
          pipe(
            tap(() =>
              patchState(store, {
                loading: true,
                error: null,
                selectedDocumentDetail: null,
                editedContent: null
              })
            ),
            switchMap((id) =>
              documentService.getDocument(id).pipe(
                tap({
                  next: (selectedDocumentDetail) =>
                    patchState(store, {
                      selectedDocumentDetail,
                      loading: false
                    })
                }),
                catchError(() => {
                  patchState(store, {
                    error: "Failed to load document",
                    loading: false
                  });
                  return of(null);
                })
              )
            )
          )
        ),
        loadPublicDocument: rxMethod<string>(
          pipe(
            tap(() =>
              patchState(store, {
                loading: true,
                error: null,
                selectedDocumentDetail: null,
                editedContent: null
              })
            ),
            switchMap((id) =>
              documentService.getPublicDocument(id).pipe(
                tap({
                  next: (selectedDocumentDetail) =>
                    patchState(store, {
                      selectedDocumentDetail,
                      loading: false
                    })
                }),
                catchError(() => {
                  patchState(store, {
                    error: "Failed to load public document",
                    loading: false
                  });

                  return of(null);
                })
              )
            )
          )
        ),
        resetDocument: (): void => {
          patchState(store, { selectedDocumentDetail: null });
        },
        setNavigatingSafely: (isNavigating: boolean): void => {
          patchState(store, { isNavigatingSafely: isNavigating });
        },
        updateDocumentContent: rxMethod<void>(
          pipe(
            tap(() => patchState(store, { loading: true })),
            switchMap(() => {
              const doc = store.selectedDocumentDetail();
              const content = store.editedContent();
              if (!doc || content === null) {
                handleUpdateError("No document or content to update.");
                return of(null);
              }

              const normalized = content || "";
              return documentService.updateDocumentContent(doc.id, normalized).pipe(
                tap({
                  next: (updatedDoc) => handleUpdateSuccess(updatedDoc)
                }),
                catchError(() => {
                  handleUpdateError("Failed to update content.");
                  return of(null);
                })
              );
            })
          )
        ),
        updateAiModel: rxMethod<{ documentId: string; aiModelId: string }>(
          pipe(
            switchMap(({ documentId, aiModelId }) => {
              // save current document
              return documentService.updateAiModel(documentId, aiModelId).pipe(
                tap({
                  next: (updatedDoc) => {
                    handleUpdateSuccess(updatedDoc);
                    toast.success("AI model updated successfully");
                  }
                }),
                catchError(() => {
                  toast.error("Failed to update AI model");
                  return of(null);
                })
              );
            })
          )
        ),
        notifyDocumentUpdated: (id: string): void => {
          patchState(store, { updatedDocument: { id, timestamp: Date.now() } });
        }
      };
    }
  )
);
